<template>
  <div>
    <div
      class="mb-6 py-3 border-b-2 border-40 flex justify-between items-center"
    >
      <h2 class="heading-3 normal-case text-2xl font-bold leading-none">UnrankedSmurfs Checkout</h2>
      <div class="flex">
        <div
          :class="[
            'text-md rounded border px-2',
            step === Steps.PAYMENT ? 'cursor-pointer' : '',
            summaryStepStyle,
          ]"
          @click="step = Steps.SUMMARY"
        >
          1
        </div>
        <div :class="['text-md rounded border px-2 ml-2', paymentStepStyle]">
          2
        </div>
      </div>
    </div>

    <reservation-timer />

    <!-- Step 1: Summary -->
    <div v-if="step === Steps.SUMMARY" class="mt-5">
      <cart-summary />
      <apply-coupon />

      <div class="flex flex-col text-md leading-md mt-3">
        <span>INSTANT DELIVERY:</span>
        <span class="text-smurfs-gold font-semibold">FREE</span>
      </div>

      <div class="flex flex-col text-md leading-md mt-2">
        <span>CART SUBTOTAL:</span>
        <span class="text-smurfs-gold font-semibold">USD {{ total.toFixed(2) }}</span>
      </div>

      <div
        class="mt-4 text-absolute-white bg-smurfs-gold py-2 text-center cursor-pointer"
        @click="step = Steps.PAYMENT"
      >
        Continue to Payment
      </div>
    </div>

    <!-- Step 2: Payment details -->
    <div v-if="step === Steps.PAYMENT" class="mt-5">
      <div class="flex flex-col">
        <h3 class="heading-4 text-xl font-bold leading-none">Details for delivery</h3>

        <!-- Name input -->
        <div class="flex items-center relative mt-4">
          <span class="absolute left-0 ml-2">
            <i class="fa fa-user" />
          </span>
          <input v-model="name" type="text" placeholder="Name (required)" />
        </div>

        <!-- Email input -->
        <div class="flex items-center relative mt-5">
          <span class="absolute left-0 ml-2">
            <i class="fa fa-envelope" />
          </span>
          <input
            v-model.trim="email"
            name="email"
            type="text"
            placeholder="Email Address (required)"
          />
        </div>
      </div>

      <h3 class="heading-4 mt-8 text-xl font-bold leading-none">Payment provider</h3>

      <!-- Provider cards -->

      <div
        v-for="provider in Object.values(PAYMENT_PROVIDERS)"
        :key="provider.id"
        :class="[
          'bg-smog-grey text-white flex items-center py-2 pr-4 mt-4 cursor-pointer flex-wrap',
          `border-2 border-${getProviderColor(provider.id)} px-1 rounded-lg`,
        ]"
        @click="selectedProvider = provider.id"
      >
        <div
          class="flex flex-1 justify-center items-center mt-1 mb-1"
          style="min-width: 139px"
        >
          <img
            :src="provider.image"
            style="height: 50px; width: 139px !important"
          />
        </div>
        <div class="flex items-center mb-2 mt-2 ml-4" style="min-width: 216px">
          <div
            v-for="method in provider.methods"
            class="w-12 mr-2 flex items-center"
          >
            <img :src="method" />
          </div>
        </div>

        <div class="flex flex-1 justify-end mt-2 mb-2 ml-4">
          <div class="flex justify-end" style="min-width: 100px">
            <div v-if="provider.recommended">
              <span
                :class="`bg-${getProviderColor(
                  provider.id
                )} text-almost-black rounded px-2 py-px text-sm leading-sm`"
              >
                Recommended
              </span>
            </div>
            <div v-if="provider.moreMethodsLabel">
              <span>{{ provider.moreMethodsLabel }}</span>
            </div>
          </div>
          <div class="flex justify-end items-center ml-2">
            <div
              :class="`rounded-full border-2 border-${getProviderColor(
                provider.id
              )} p-px`"
            >
              <div
                :class="`rounded-full bg-${getProviderColor(
                  provider.id
                )} h-3 w-3 m-px`"
              />
            </div>
          </div>
        </div>
      </div>

      <label class="text-sm leading-sm mt-4 flex items-start">
        <input
          type="checkbox"
          class="mr-2 mt-2"
          :value="subscribeToMailingList"
          :checked="subscribeToMailingList"
          @input="updateMailingListSubscription"
        >
        Tick here if you would like to receive our latest offers and updates
        through our mailing list.
      </label>

      <!-- Pay button -->
      <div
        :class="[
          'mt-8 py-2 text-center',
          canSubmit
            ? 'text-absolute-white bg-smurfs-gold cursor-pointer'
            : 'text-60 bg-metallic-grey cursor-not-allowed',
        ]"
        @click="handlePayButtonClick"
      >
        Buy Now
      </div>

      <p class="text-sm text-center mt-1">
        By placing your order, you agree to the UnrankedSmurfs
        <a href="/terms-and-conditions" target="_blank" class="font-bold">
          Terms and Conditions
        </a>
      </p>
    </div>

    <error-modal
      :error="error"
      :is-open="errorModalOpen"
      @close="closeModal()"
    />
  </div>
</template>

<script>
import { checkoutMixin } from '../../shared/mixins/checkoutMixin';

import CartSummary from './CartSummary.vue';
import ErrorModal from './ErrorModal.vue';
import ReservationTimer from './ReservationTimer.vue';
import store from '../../shared/store';
import { mapGetters, mapState } from 'vuex';
import { sha256 } from 'js-sha256';
import { getParsedQueryString } from "../pages/components/utils/url";

const Steps = {
  SUMMARY: 'SUMMARY',
  PAYMENT: 'PAYMENT',
};

const INACTIVE_STEP_STYLE = 'border-50 bg-50-50';
const ACTIVE_STEP_STYLE = 'border-smurfs-gold bg-smurfs-gold-50';
const COMPLETED_STEP_STYLE = 'border-ok-fam-green bg-ok-fam-green-50';

export default {
  components: {
    CartSummary,
    ErrorModal,
    ReservationTimer,
  },

  mixins: [checkoutMixin],

  data() {
    return {
      totalPrice: 0,
      step: Steps.SUMMARY,
      selectedProvider: 'stripe',
      Steps,
      PAYMENT_PROVIDERS: {
        stripe: {
          id: 'stripe',
          image: '/images/payment-providers/logo_stripe.png',
          methods: [
            '/images/misc/payment-methods/visa.png',
            '/images/misc/payment-methods/mastercard.png',
            '/images/misc/payment-methods/googlepay.png',
          ],
          moreMethodsLabel: '',
          handler: this.handlePayWithStripe,
          recommended: true,
        },
        // payop: {
        //   id: 'payop',
        //   image: '/images/payment-providers/logo_payop.png',
        //   methods: [
        //     '/images/misc/payment-methods/skrill.png',
        //     '/images/misc/payment-methods/bitcoin.png',
        //     '/images/misc/payment-methods/paysafecard.png',
        //     '/images/misc/payment-methods/klarna.png',
        //   ],
        //   moreMethodsLabel: '+200 more',
        //   handler: this.handlePayWithPayop,
        // },
        paymentwall: {
          id: 'paymentwall',
          image: '/images/payment-providers/logo_paymentwall.svg',
          methods: [
            '/images/misc/payment-methods/bitcoin.png',
            '/images/misc/payment-methods/ideal.png',
            '/images/misc/payment-methods/giropay.png',
          ],
          moreMethodsLabel: '+100s more',
          handler: this.handlePayWithPaymentwall,
        },
        // skrill: {
        //   id: 'skrill',
        //   image: '/images/payment-providers/logo_skrill.png',
        //   methods: [
        //     '/images/misc/payment-methods/paysafecard.png',
        //     '/images/misc/payment-methods/neteller.png',
        //     '/images/misc/payment-methods/rapid_transfer.png',
        //   ],
        //   moreMethodsLabel: '',
        //   handler: this.handlePayWithSkrill,
        // },
        // paysafe: {
        //   id: 'paysafe',
        //   image: '/images/payment-providers/logo_paysafe.png',
        //   methods: [
        //     '/images/misc/payment-methods/visa.png',
        //     '/images/misc/payment-methods/mastercard.png',
        //   ],
        //   moreMethodsLabel: '',
        //   handler: this.handlePayWithPaysafe,
        // },
      },
    };
  },

  computed: {
    ...mapState('cart', ['subscribeToMailingList']),
    ...mapGetters('cart', ['total']),
    summaryStepStyle() {
      return this.step === Steps.SUMMARY
        ? ACTIVE_STEP_STYLE
        : COMPLETED_STEP_STYLE;
    },

    paymentStepStyle() {
      return this.step === Steps.PAYMENT
        ? ACTIVE_STEP_STYLE
        : INACTIVE_STEP_STYLE;
    },
  },

  methods: {
    getProviderColor(providerId) {
      return providerId === this.selectedProvider ? 'smurfs-gold' : '40';
    },

    handlePayButtonClick() {
      const provider = this.PAYMENT_PROVIDERS[this.selectedProvider];
      const { cart } = store.state;

      if (!this.canSubmit) {
        return;
      }

      if (window.dataLayer) {
        dataLayer.push({
          event: 'checkoutDetailsAdded',
          ecommerce: {
            userDetails: {
              name: this.name,
              email: this.email,
              externalId: sha256(this.email),
            },
            products: [
              cart.items.map(({ description, price, extra }) => ({
                name: description,
                id: extra.id,
                category: extra.type,
                price: price,
              })),
            ],
          },
        });
      }

      if (window.dataLayer && this.subscribeToMailingList) {
        dataLayer.push({
          event: 'subscribeToNewsLetter',
          newsLetterSubscription: {
            add: {
              user: {
                name: this.name,
                email: this.email,
                externalId: sha256(this.email),
              },
            },
          },
        });
      }

      provider.handler();
    },
  },
};
</script>

<style lang="scss" scoped>
input {
  @apply flex-1;
  @apply border-t border-b border-honestly-white;
  @apply bg-transparent text-absolute-white;
  @apply h-12 pl-10;
  @apply outline-none;

  &::placeholder {
    @apply font-normal text-50;
  }
}
</style>
